import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { RatingStatsPanel, RatingForm, RatingCard } from './ratings/RatingComponents';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const RatingSystem = ({ targetId, targetType, targetName }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [formData, setFormData] = useState({ score: 75, review_text: '', job_id: '', photo_urls: [] });
  const [submitting, setSubmitting] = useState(false);
  const [responding, setResponding] = useState(null);
  const [responseText, setResponseText] = useState('');

  useEffect(() => { fetchRatings(); }, [targetId, targetType]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRatings = async () => {
    try { setStats((await api.get(`/ratings/${targetType}/${targetId}`)).data); }
    catch (error) { /* Ratings fetch handled in UI */ }
    finally { setLoading(false); }
  };

  const handleSubmitRating = async (e) => {
    e.preventDefault();
    if (formData.review_text.trim().length < 10) { toast.error('Review must be at least 10 characters long'); return; }
    setSubmitting(true);
    try {
      await api.post('/ratings/submit', { target_id: targetId, target_type: targetType, score: formData.score, review_text: formData.review_text, job_id: formData.job_id || null, photo_urls: formData.photo_urls });
      toast.success('Rating submitted!'); setShowSubmitForm(false);
      setFormData({ score: 75, review_text: '', job_id: '', photo_urls: [] }); fetchRatings();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit rating'); }
    finally { setSubmitting(false); }
  };

  const handleRespondToRating = async (ratingId) => {
    if (!responseText.trim()) { toast.error('Response cannot be empty'); return; }
    try {
      await api.post(`/ratings/${ratingId}/respond`, { response_text: responseText });
      toast.success('Response posted!'); setResponding(null); setResponseText(''); fetchRatings();
    } catch { toast.error('Failed to post response'); }
  };

  if (loading) return (
    <div className="text-center py-8">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="rating-system">
      <div className="bg-white border border-slate-200 rounded-md p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Ratings & Reviews</h2>
          <button onClick={() => setShowSubmitForm(!showSubmitForm)}
            className="bg-blue-500 text-white px-4 py-2 rounded-md font-semibold hover:bg-blue-600 transition-all" data-testid="write-review-btn">
            Write a Review
          </button>
        </div>
        <RatingStatsPanel stats={stats} />
        <div className="bg-slate-50 rounded-md p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Average Rating</span>
            <span className="text-lg font-bold text-slate-900">{(stats?.total_ratings || 0) > 0 ? (stats?.average_score || 0) : 100}/100</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-3">
            <div className="h-3 rounded-full transition-all bg-blue-500" style={{ width: `${(stats?.total_ratings || 0) > 0 ? (stats?.average_score || 0) : 100}%` }} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSubmitForm && <RatingForm formData={formData} setFormData={setFormData} onSubmit={handleSubmitRating}
          submitting={submitting} onCancel={() => setShowSubmitForm(false)} targetName={targetName} />}
      </AnimatePresence>

      <div className="space-y-4">
        {!stats?.recent_ratings?.length ? (
          <div className="bg-white border border-slate-200 rounded-md p-8 text-center">
            <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-600">No reviews yet. Be the first to review!</p>
          </div>
        ) : (
          stats.recent_ratings.map((rating, index) => (
            <RatingCard key={rating.id} rating={rating} index={index} targetName={targetName}
              responding={responding} setResponding={setResponding} responseText={responseText}
              setResponseText={setResponseText} onRespond={handleRespondToRating} />
          ))
        )}
      </div>
    </div>
  );
};

export default RatingSystem;
